import { createRef } from 'react';
import { EditorView, Decoration, DecorationSet } from 'prosemirror-view';
import {
  EditorState,
  Plugin,
  PluginKey,
  TextSelection,
  Selection,
} from 'prosemirror-state';
import DiffMatchPatch from 'diff-match-patch';
import { DOMParser } from 'prosemirror-model';
import { exampleSetup } from 'prosemirror-example-setup';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { inputRules } from 'prosemirror-inputrules';
import * as jsonpatch from 'fast-json-patch';
import schema from './schema';
import { editorCommands, availableCommands } from './editorCommands.js';
import { editorKeymap, keymaps, baseKeymaps } from '../../dryKeyboardMapping';
import { linkInputRule, linkHandlePaste, linkKeyboardPlugin } from './linkPlugin';
import { createLinkTooltipPlugin } from './linkTooltipPlugin';
import { createSlashCommandPlugin } from './slashCommandPlugin';
import { parseDocValue } from './parseDocValue';

const historyPreviewKey = new PluginKey('historyPreview');

const defaultEditorState = {
  schema: schema,
  plugins: exampleSetup({
    schema: schema,
    menuBar: false,
  }),
};

const handleClickOn = (editorView, pos, node, nodePos, event) => {
  if (event.target.classList.contains('todo-checkbox')) {
    editorView.dispatch(toggleTodoItemAction(editorView.state, nodePos, node));
    return true;
  }
};

const toggleTodoItemAction = (state, pos, todoItemNode) => {
  return state.tr.setNodeMarkup(pos, null, {
    checked: !todoItemNode.attrs.checked,
  });
};


class DryEditorClass {
  constructor() {
    this.editorRef = createRef();
    this.editorView = null;
    this.initializeEditorView = this.initializeEditorView.bind(this);
    this.initializeNewEditorView = this.initializeNewEditorView.bind(this);
    this.setEditorRef = this.setEditorRef.bind(this);
    this.getEditorRef = this.getEditorRef.bind(this);
    this.getEditorView = this.getEditorView.bind(this);
    this.getEditorState = this.getEditorState.bind(this);
    this.getEditorStateJSON = this.getEditorStateJSON.bind(this);
    this.getEditorStateText = this.getEditorStateText.bind(this);
    this.dispatchedTransactions = this.dispatchedTransactions.bind(this);
    this.registeredTransactionHandlers = new Map();
    this.registerTransactionHandler =
      this.registerTransactionHandler.bind(this);
    this.updateProps = this.updateProps.bind(this);
    this.parseDOMToDocument = this.parseDOMToDocument.bind(this);
    this.parseHtmlStringToDom = this.parseHtmlStringToDom.bind(this);
    this.parseJSONToSnippetText = this.parseJSONToSnippetText.bind(this);
    this.getDispatch = this.getDispatch.bind(this);
    this.execCommands = this.execCommands.bind(this);
    this.getEditorCommands = this.getEditorCommands.bind(this);
    this.getCursorPosition = this.getCursorPosition.bind(this);
    this.setCursorPosition = this.setCursorPosition.bind(this);
    this.getDocPatch = this.getDocPatch.bind(this);
    this.confirmSave = this.confirmSave.bind(this);
    this.resetForNote = this.resetForNote.bind(this);
    this.setActiveNoteId = this.setActiveNoteId.bind(this);
    this.showHistoryPreview = this.showHistoryPreview.bind(this);
    this.clearHistoryPreview = this.clearHistoryPreview.bind(this);
    this.setContent = this.setContent.bind(this);

    // Delta save state: baseline doc JSON string and server-confirmed version
    this._lastConfirmedDocJSON = null;
    this._confirmedVersion = 0;
    // Track the currently active note so stale cross-note saves don't corrupt the baseline
    this._activeNoteId = null;

    // Pending cursor-restore rAF handle — cancelled when a new note initializes
    // so Note A's rAF can never fire on Note B's editor view.
    this._pendingCursorRaf = null;

    // History preview: decoration plugin and state flag
    this._isPreviewingHistory = false;
    this._historyPreviewPlugin = new Plugin({
      key: historyPreviewKey,
      state: {
        init: () => DecorationSet.empty,
        apply: (tr, set) => {
          const meta = tr.getMeta(historyPreviewKey);
          if (meta?.decorations !== undefined) return meta.decorations;
          return set.map(tr.mapping, tr.doc);
        },
      },
      props: {
        decorations: (state) => historyPreviewKey.getState(state),
      },
    });
  }

  initializeEditorView({
    editorRef = null,
    focus = true,
    value = '{}',
    className = 'dry-editor-content',
    cursorPosition = 0,
    globalServices = {},
  }) {
    const parsedDoc = parseDocValue(value);
    const document = schema.nodeFromJSON(parsedDoc);

    if (editorRef) {
      this.editorRef = editorRef;
    }

    // Initialize Prosemirror editor
    this.editorView = new EditorView(this.editorRef, {
      state: EditorState.create({
        ...defaultEditorState,
        doc: document,
        plugins: [
          history(),
          linkKeyboardPlugin, // must precede list keymaps to take priority on Tab/Backspace
          keymap(editorKeymap(keymaps, globalServices)),
          keymap(editorKeymap(baseKeymaps, globalServices)),
          inputRules({ rules: [linkInputRule] }),
          createLinkTooltipPlugin(globalServices?.modalState?.send, globalServices?.appState?.send),
          createSlashCommandPlugin(),
          this._historyPreviewPlugin,
        ],
      }),
      dispatchTransaction: this.dispatchedTransactions,
      focus,
      attributes: {
        class: className,
        id: 'dry-editor',
      },
      handleClickOn,
      handlePaste: linkHandlePaste,
      handleDOMEvents: {
        click: (view, event) => {
          const li = event.target.closest('li.check-list-item');
          if (li) {
            const rect = li.getBoundingClientRect();
            if (event.clientX - rect.left <= 22) {
              event.preventDefault();
              try {
                const $pos = view.state.doc.resolve(view.posAtDOM(li, 0));
                const checkListItemType = view.state.schema.nodes['check_list_item'];
                for (let d = $pos.depth; d >= 0; d--) {
                  const n = $pos.node(d);
                  if (n.type === checkListItemType) {
                    const itemPos = $pos.before(d);
                    view.dispatch(
                      view.state.tr.setNodeMarkup(itemPos, null, { checked: !n.attrs.checked })
                    );
                    return true;
                  }
                }
              } catch (e) {
                return false;
              }
            }
          }
          return false;
        },
      },
    });

    // Attach Prosemirror to DOM
    this.editorRef.current.appendChild(this.editorView.dom);

    // Cancel any pending rAF from a previous note so it cannot fire on this
    // editor view. Then schedule cursor placement after the first layout paint
    // so the scrollable container has its full dimensions for scrollIntoView().
    if (this._pendingCursorRaf !== null) {
      cancelAnimationFrame(this._pendingCursorRaf);
    }
    this._pendingCursorRaf = requestAnimationFrame(() => {
      this._pendingCursorRaf = null;
      this.setCursorPosition(cursorPosition, false);
    });
    // Make this class available on the global window
    window.DRY = window.DRY || {};
    window.DRY.dryEditorClass = this;
  }

  initializeNewEditorView({ editorRef }) {
    this.editorView.destroy();
    this.initializeEditorView({ editorRef });
  }

  setEditorRef() {
    return (this.editorRef = createRef());
  }

  getEditorRef() {
    return this.editorRef;
  }

  getEditorView() {
    return this.editorView;
  }

  getEditorState() {
    return this.editorView.state;
  }

  getEditorStateJSON(space = 0) {
    return JSON.stringify(this.editorView.state.doc.toJSON(), null, space);
  }

  getEditorStateText(length) {
    if (length) {
      return this.editorView.state.doc.textContent.slice(0, length);
    }
    return this.editorView.state.doc.textContent;
  }

  dispatchedTransactions(transaction) {
    const { state, transactions } =
      this.editorView.state.applyTransaction(transaction);
    this.editorView.updateState(state);

    const docChanged = transactions.some((tr) => tr.docChanged);
    const selectionSet = transactions.some((tr) => tr.selectionSet);
    if (docChanged || selectionSet) {
      for (const [key, value] of this.registeredTransactionHandlers) {
        value(state, { docChanged, selectionSet });
      }
    }
  }

  registerTransactionHandler({ name, method }) {
    this.registeredTransactionHandlers.set(name, method);
  }

  updateProps(props) {
    this.editorView.update(props);
  }

  parseDOMToDocument(value) {
    return DOMParser.fromSchema(schema).parse(value);
  }

  parseHtmlStringToDom(html = '') {
    const template = document.createElement('template');
    html = `<div>${html.trim()}</div>`;
    template.innerHTML = html;
    return template.content.firstChild;
  }

  parseJSONToSnippetText(value) {
    try {
      const JSONDoc = JSON.parse(value);
      const firstBlock = JSONDoc?.content?.[0];
      if (!firstBlock?.content) return '';
      const snippets = [];
      firstBlock.content.forEach((node) => {
        if (node.text) snippets.push(node.text);
      });
      return snippets.join();
    } catch (e) {
      return '';
    }
  }

  getDispatch() {
    return this.editorView.dispatch;
  }

  execCommands(commands) {
    for (let command of commands) {
      const { commandName, options } = command;
      if (editorCommands[commandName]) {
        if (options) {
          editorCommands[commandName](options);
        } else {
          editorCommands[commandName]();
        }
      } else {
        console.error(`${commandName} not found`);
      }

      this.editorView.focus();
    }
  }

  getEditorCommands() {
    return availableCommands;
  }

  getCursorPosition() {
    const state = this.getEditorState();
    const cursor = state.selection.$cursor;
    return cursor && cursor.pos ? cursor.pos : 0;
  }

  /**
   * Returns a JSON Patch (RFC 6902) array diffing the last confirmed save
   * against the current doc, or null if no baseline exists yet.
   * @param {string} currentDocJSON - stringified current doc (from getEditorStateJSON)
   */
  getDocPatch(currentDocJSON) {
    if (!this._lastConfirmedDocJSON || !currentDocJSON) return null;
    const baseline = JSON.parse(this._lastConfirmedDocJSON);
    const current = JSON.parse(currentDocJSON);
    return jsonpatch.compare(baseline, current);
  }

  /**
   * Called after a successful save (delta or full) to update the baseline.
   * @param {string} docJSON - stringified doc that was saved
   * @param {number} version - server-confirmed doc_version
   * @param {string|null} noteId - the note_id this save was for; if provided and
   *   it doesn't match _activeNoteId, the update is skipped to prevent a stale
   *   cross-note save from corrupting the delta baseline for the new note.
   */
  confirmSave(docJSON, version, noteId = null) {
    if (noteId && this._activeNoteId && noteId !== this._activeNoteId) {
      console.warn(
        `DryEditorClass.confirmSave: ignoring stale save for note ${noteId} (active: ${this._activeNoteId})`
      );
      return;
    }
    this._lastConfirmedDocJSON = docJSON;
    this._confirmedVersion = typeof version === 'number' ? version : 0;
  }

  /** Called when switching notes to reset the baseline for the new note. */
  resetForNote(docJSON, version) {
    this._lastConfirmedDocJSON = docJSON || null;
    this._confirmedVersion = typeof version === 'number' ? version : 0;
  }

  /** Called when the active note changes so confirmSave can reject stale saves. */
  setActiveNoteId(noteId) {
    this._activeNoteId = noteId || null;
  }

  setCursorPosition(position = null, shouldFocus = true) {
    try {
      const state = this.getEditorState();
      const { tr } = state;
      const documentSize = state.doc.content.size;

      let lastPosition;
      if (position != null && position > 0 && position <= documentSize) {
        // Valid saved position — restore it exactly
        lastPosition = position;
      } else if (position != null && position > 0) {
        // Position is beyond the current document (note was shortened) — clamp to end
        lastPosition = Selection.atEnd(tr.doc).from;
      } else {
        // No position (null / 0 / undefined) — start at the top
        lastPosition = documentSize > 0 ? Selection.atStart(tr.doc).from : 0;
      }

      const selection = TextSelection.create(tr.doc, lastPosition);
      if (shouldFocus) {
        this.getEditorView().focus();
      }
      this.getEditorView().dispatch(tr.setSelection(selection).scrollIntoView());
    } catch (e) {
      // Cursor placement failed (corrupt position, schema mismatch, etc.).
      // Log it but don't let it prevent the editor from mounting — the note
      // will still display, just without cursor restoration.
      console.warn('DryEditorClass: setCursorPosition failed, cursor not restored', e);
      try {
        const interactionLedger = require('../../utils/interactionLedger').default;
        interactionLedger.log(
          'noteOpenError',
          'editor',
          { position, error: e?.message },
          `Editor cursor restore failed (position ${position}): ${e?.message}`
        );
      } catch (_) {}
    }
  }

  /**
   * Show a read-only diff overlay highlighting text added to the current doc
   * since the given historical snapshot. Green decorations mark additions.
   * @param {string|object} snapshotJSON - ProseMirror doc JSON (string or parsed object)
   */
  showHistoryPreview(snapshotJSON) {
    if (!this.editorView) return;
    try {
      const snapshotParsed = parseDocValue(snapshotJSON);
      const snapshotNode = schema.nodeFromJSON(snapshotParsed);
      const snapshotText = snapshotNode.textContent;
      const currentText = this.editorView.state.doc.textContent;

      const dmp = new DiffMatchPatch();
      const diffs = dmp.diff_main(snapshotText, currentText);
      dmp.diff_cleanupSemantic(diffs);

      const posMap = this._buildTextOffsetMap(this.editorView.state.doc);
      const decorations = [];
      let currentOffset = 0;

      for (const [op, text] of diffs) {
        if (op === 1) {
          // Added in current doc since this snapshot
          const start = currentOffset;
          const end = currentOffset + text.length;
          if (start < posMap.length && end <= posMap.length) {
            const from = posMap[start];
            const to = posMap[end - 1] + 1;
            if (from < to) {
              decorations.push(Decoration.inline(from, to, { class: 'history-add' }));
            }
          }
          currentOffset += text.length;
        } else if (op === 0) {
          currentOffset += text.length;
        }
        // op === -1: deleted since snapshot — no chars in current doc, skip
      }

      const decorationSet = DecorationSet.create(this.editorView.state.doc, decorations);
      const tr = this.editorView.state.tr.setMeta(historyPreviewKey, { decorations: decorationSet });
      this.editorView.dispatch(tr);
      this.editorView.setProps({ editable: () => false });
      this._isPreviewingHistory = true;
    } catch (e) {
      console.warn('DryEditorClass.showHistoryPreview: failed', e);
    }
  }

  /**
   * Replace the editor's document with the given ProseMirror JSON snapshot.
   * Used by the restore-from-history flow. Preserves all existing plugins.
   * Call resetForNote() afterwards to clear the delta baseline and force a full save.
   * @param {string|object} docJSON
   */
  setContent(docJSON) {
    if (!this.editorView) return;
    try {
      const parsedDoc = parseDocValue(docJSON);
      const doc = schema.nodeFromJSON(parsedDoc);
      const newState = EditorState.create({
        schema,
        doc,
        plugins: this.editorView.state.plugins,
      });
      this.editorView.updateState(newState);
    } catch (e) {
      console.warn('DryEditorClass.setContent: failed', e);
    }
  }

  /** Remove history diff decorations and restore editing. */
  clearHistoryPreview() {
    if (!this.editorView || !this._isPreviewingHistory) return;
    const tr = this.editorView.state.tr.setMeta(historyPreviewKey, { decorations: DecorationSet.empty });
    this.editorView.dispatch(tr);
    this.editorView.setProps({ editable: () => true });
    this._isPreviewingHistory = false;
  }

  /**
   * Build a map from text-content character offset → ProseMirror position.
   * ProseMirror positions include node boundaries so this map is necessary to
   * convert diff-match-patch character spans to decoration `from`/`to` values.
   */
  _buildTextOffsetMap(doc) {
    const positions = [];
    doc.descendants((node, pos) => {
      if (node.isText) {
        for (let i = 0; i < node.text.length; i++) {
          positions.push(pos + i);
        }
      }
    });
    return positions;
  }
}

const dryEditorClass = new DryEditorClass();
export default dryEditorClass;
